import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Mail, AlertCircle, CheckCircle2, Calendar } from 'lucide-react'
import { isValidEmail, normalizeEmail } from '@/utils/validation'

const WEBHOOK_URL = 'https://n8n-usinerie.up-automatisation.com/webhook/34bc68f3-016f-4af6-b375-3a2da2b18e8b'

export default function Unsubscribe() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isValidEmail(email)) {
      setError('Veuillez saisir une adresse email valide')
      return
    }

    setLoading(true)

    try {
      const normalizedEmail = normalizeEmail(email)

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      })

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`)
      }

      setSuccess(true)
    } catch (err) {
      console.error('Erreur désinscription:', err)
      setError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo/Icône */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 bg-blue-500 rounded-xl flex items-center justify-center">
            <Calendar className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Titre et sous-titre */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Désinscription</h1>
          <p className="text-gray-600">
            Renseignez votre adresse email pour vous désinscrire des newsletters de l'Usinerie Partners.
          </p>
        </div>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            {success ? (
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-gray-800 font-medium">Désinscription enregistrée</p>
                <p className="text-sm text-gray-500">
                  Votre demande a bien été prise en compte. Vous ne recevrez plus nos newsletters.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Message d'erreur */}
                {error && (
                  <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}

                {/* Champ Email */}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Adresse email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 h-12"
                      placeholder="votre@email.com"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Bouton */}
                <Button
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                  disabled={loading}
                >
                  {loading ? 'Traitement en cours...' : 'Me désinscrire'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
